import apiClient from '../utils/apiClient';

/**
 * QC Session API Services
 *
 * Từ bản refactor phân phối QC: phiên AUTO sinh ngay lúc NV nhận nhiệm vụ (vào thẳng
 * IN_PROGRESS, không còn trạng thái OPEN chờ ai đó join), mỗi phiên là 1 gói việc cỡ
 * cố định `items_target` mục gộp cả 4 nguồn. Các cột *_quota nay là SỐ MỤC ĐÃ PHÂN BỔ
 * chứ không phải hạn mức cấu hình.
 */

export const qcSessionAPI = {
  /**
   * Lấy danh sách QC Sessions với phân trang và lọc
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.branch_id - Lọc theo chi nhánh
   * @param {'AUTO'|'MANUAL'} params.type - Lọc theo loại session
   * @param {'OPEN'|'IN_PROGRESS'|'COMPLETED'} params.status - Lọc theo trạng thái
   * @param {string} params.date_from - Lọc theo ngày tạo phiên (YYYY-MM-DD)
   * @param {string} params.date_to - Lọc theo ngày tạo phiên (YYYY-MM-DD)
   * @param {string} params.qc_employee_id - Lọc theo QC viên
   * @param {boolean} params.with_availability - Kèm số mục thực sự còn review được
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<{data: Array, meta: Object}>} Danh sách QC Sessions + meta phân trang
   */
  getQcSessions: async (params = {}) => {
    const {
      branch_id,
      type,
      status,
      date_from,
      date_to,
      qc_employee_id,
      with_availability,
      page,
      limit,
    } = params;
    return await apiClient.get({
      url: '/qc-sessions',
      params: {
        branch_id,
        type,
        status,
        date_from,
        date_to,
        qc_employee_id,
        with_availability,
        page,
        limit,
      },
    });
  },

  /**
   * Lượng việc đang chờ QC theo chi nhánh + số phiên đã soi trong kỳ.
   * Đây là số liệu để đánh giá ĐỘ PHỦ của QC (backlog còn bao nhiêu so với đã soi).
   * @param {number} daysBack - Số ngày tính ngược (mặc định 30)
   */
  getQcBacklog: async (daysBack) => {
    return await apiClient.get({
      url: '/qc-sessions/backlog',
      params: { days_back: daysBack },
    });
  },

  /**
   * Lấy thông tin chi tiết QC Session theo ID
   * @param {string} sessionId - ID session
   * @returns {Promise<Object>} Thông tin chi tiết session
   */
  getQcSessionById: async (sessionId) => {
    return await apiClient.get({
      url: `/qc-sessions/${sessionId}`,
    });
  },

  /**
   * Tạo Manual Session (Admin/Manager) — admin chọn chi nhánh + khoảng ngày cần soi.
   * @param {Object} sessionData
   * @param {string} sessionData.branch_id - ID chi nhánh
   * @param {string} sessionData.planned_task_date - Ngày phiên (YYYY-MM-DD)
   * @param {string} sessionData.review_date_from - Lấy việc từ ngày (YYYY-MM-DD)
   * @param {string} sessionData.review_date_to - Lấy việc tới ngày (YYYY-MM-DD)
   * @param {number} [sessionData.items_target] - Tổng số mục; bỏ trống → theo cấu hình
   * @returns {Promise<Object>} Thông tin session vừa tạo
   */
  createQcSession: async (sessionData) => {
    return await apiClient.post({
      url: '/qc-sessions',
      data: sessionData,
    });
  },

  /**
   * QC Join Session (chỉ còn dùng cho phiên MANUAL đang OPEN)
   * @param {string} sessionId - ID session
   */
  joinQcSession: async (sessionId) => {
    return await apiClient.post({
      url: `/qc-sessions/${sessionId}/join`,
    });
  },

  /**
   * Complete Session
   * @param {string} sessionId - ID session
   */
  completeQcSession: async (sessionId) => {
    return await apiClient.post({
      url: `/qc-sessions/${sessionId}/complete`,
    });
  },

  /**
   * Thoát/thu hồi phiên đang dở — trả các mục chưa chấm về pool cho người khác.
   * Phiên chưa chấm mục nào sẽ bị xoá; phiên đã chấm dở được đóng COMPLETED.
   * @param {string} sessionId - ID session
   */
  leaveQcSession: async (sessionId) => {
    return await apiClient.post({
      url: `/qc-sessions/${sessionId}/leave`,
    });
  },

  /**
   * Sửa QC Session (chỉ khi status = OPEN và chưa có mục nào được gán)
   * @param {string} sessionId - ID session
   * @param {Object} sessionData
   * @param {string} [sessionData.planned_task_date] - Ngày phiên (YYYY-MM-DD)
   * @param {string} [sessionData.review_date_from] - Lấy việc từ ngày (YYYY-MM-DD)
   * @param {string} [sessionData.review_date_to] - Lấy việc tới ngày (YYYY-MM-DD)
   * @param {number} [sessionData.items_target] - Tổng số mục
   */
  updateQcSession: async (sessionId, sessionData) => {
    return await apiClient.put({
      url: `/qc-sessions/${sessionId}`,
      data: sessionData,
    });
  },

  /**
   * Xóa QC Session (chỉ khi status = OPEN và chưa có mục nào được gán)
   * @param {string} sessionId - ID session
   */
  deleteQcSession: async (sessionId) => {
    return await apiClient.delete({
      url: `/qc-sessions/${sessionId}`,
    });
  },
};
